import { useQuery } from "@tanstack/react-query";
import { ChevronLeft, ChevronRight } from "lucide-react";
import { type ReactNode, useState } from "react";
import { adminApi } from "@/api/admin.api";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { type Column, DataTable } from "./ui/DataTable";
import { PageHeader, useDebouncedValue } from "./ui/PageHeader";

const PAGE_SIZE = 50;

const formatWhen = (iso: string) =>
  new Date(iso).toLocaleString(undefined, { dateStyle: "medium", timeStyle: "short" });

const adminName = (log: any) => log.adminUser?.name || log.adminUser?.email || log.adminUserId;

/** A day picked in a date input, as the first or last instant of that day in local time. */
const dayBound = (day: string, end: boolean) =>
  day ? new Date(`${day}T${end ? "23:59:59.999" : "00:00:00"}`).toISOString() : undefined;

export function AuditLogs() {
  const [filters, setFilters] = useState({ action: "", resource: "", from: "", to: "" });
  const [page, setPage] = useState(0);
  const [open, setOpen] = useState<any | null>(null);
  const settled = useDebouncedValue(filters);

  const { data: logs, isFetching, error } = useQuery({
    queryKey: ["admin-audit", settled, page],
    queryFn: () =>
      adminApi.listAuditLogs({
        limit: PAGE_SIZE,
        offset: page * PAGE_SIZE,
        action: settled.action.trim() || undefined,
        resource: settled.resource.trim() || undefined,
        startDate: dayBound(settled.from, false),
        endDate: dayBound(settled.to, true),
      }),
    placeholderData: (previous) => previous,
  });

  const setFilter = (key: keyof typeof filters, value: string) => {
    setFilters((f) => ({ ...f, [key]: value }));
    setPage(0);
  };

  const columns: Column<any>[] = [
    { key: "when", header: "When", className: "whitespace-nowrap", cell: (l) => formatWhen(l.timestamp) },
    { key: "admin", header: "Admin", cell: (l) => adminName(l) },
    { key: "action", header: "Action", cell: (l) => <code className="font-mono text-xs">{l.action}</code> },
    {
      key: "resource",
      header: "Resource",
      cell: (l) => (
        <span>
          {l.resource}
          {l.resourceId && <code className="ml-2 font-mono text-xs text-slate-500">{l.resourceId}</code>}
        </span>
      ),
    },
    {
      key: "details",
      header: <span className="sr-only">Details</span>,
      className: "text-right",
      cell: (l) => (
        <Button variant="ghost" size="sm" onClick={() => setOpen(l)}>
          Details
        </Button>
      ),
    },
  ];

  return (
    <div className="max-w-6xl">
      <PageHeader title="Audit log" description="Every recorded admin action across both products, newest first." />
      <div className="mb-4 flex flex-wrap items-end gap-3">
        <FilterField label="Action">
          <Input placeholder="e.g. refund" value={filters.action} onChange={(e) => setFilter("action", e.target.value)} />
        </FilterField>
        <FilterField label="Resource">
          <Input placeholder="e.g. feature" value={filters.resource} onChange={(e) => setFilter("resource", e.target.value)} />
        </FilterField>
        <FilterField label="From">
          <Input type="date" value={filters.from} onChange={(e) => setFilter("from", e.target.value)} />
        </FilterField>
        <FilterField label="To">
          <Input type="date" value={filters.to} onChange={(e) => setFilter("to", e.target.value)} />
        </FilterField>
      </div>
      <DataTable
        label="Audit log"
        columns={columns}
        rows={logs}
        rowKey={(l) => l.id}
        isLoading={!logs && isFetching}
        error={error}
        emptyMessage="No admin actions match these filters."
        pageSize={null}
      />
      <div className="mt-3 flex items-center justify-end gap-2 text-xs text-slate-600">
        <span>Page {page + 1}</span>
        <Button variant="outline" size="sm" aria-label="Newer" disabled={page === 0 || isFetching} onClick={() => setPage(page - 1)}>
          <ChevronLeft className="h-4 w-4" />
        </Button>
        <Button
          variant="outline"
          size="sm"
          aria-label="Older"
          disabled={(logs?.length ?? 0) < PAGE_SIZE || isFetching}
          onClick={() => setPage(page + 1)}
        >
          <ChevronRight className="h-4 w-4" />
        </Button>
      </div>

      <Dialog open={!!open} onOpenChange={(next) => !next && setOpen(null)}>
        <DialogContent className="max-w-2xl">
          {open && (
            <>
              <DialogHeader>
                <DialogTitle className="font-mono text-base">{open.action}</DialogTitle>
                <DialogDescription>
                  {adminName(open)} · {formatWhen(open.timestamp)}
                </DialogDescription>
              </DialogHeader>
              <dl className="grid grid-cols-[8rem_1fr] gap-x-4 gap-y-2 text-sm">
                <dt className="text-slate-500">Resource</dt>
                <dd>
                  {open.resource} {open.resourceId && <code className="font-mono text-xs">{open.resourceId}</code>}
                </dd>
                <dt className="text-slate-500">IP address</dt>
                <dd>{open.ipAddress || "—"}</dd>
                {open.notes && (
                  <>
                    <dt className="text-slate-500">Notes</dt>
                    <dd>{open.notes}</dd>
                  </>
                )}
              </dl>
              <JsonBlock label="Before" value={open.changesBefore} />
              <JsonBlock label="After" value={open.changesAfter} />
              <JsonBlock label="Other details" value={open.metadata} />
            </>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}

function FilterField({ label, children }: { label: string; children: ReactNode }) {
  return (
    <label className="block w-44 space-y-1 text-xs font-medium text-slate-600">
      <span>{label}</span>
      {children}
    </label>
  );
}

function JsonBlock({ label, value }: { label: string; value: unknown }) {
  const empty = value == null || (typeof value === "object" && Object.keys(value as object).length === 0);
  if (empty) return null;
  return (
    <div>
      <div className="mb-1 text-xs font-medium uppercase tracking-wide text-slate-500">{label}</div>
      <pre className="max-h-56 overflow-auto rounded bg-slate-50 p-3 text-xs">{JSON.stringify(value, null, 2)}</pre>
    </div>
  );
}
